import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Slider } from './ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Settings, Waves } from 'lucide-react';

const PlayerSettings = ({ settings, onSettingsChange }) => {
  const [open, setOpen] = useState(false);
  const [localSettings, setLocalSettings] = useState(settings);

  const handleSave = () => {
    onSettingsChange(localSettings);
    setOpen(false);
  };

  const updateSetting = (key, value) => {
    setLocalSettings(prev => ({ ...prev, [key]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          data-testid="player-settings-button"
          variant="outline"
          size="sm"
          className="border-sunset-orange/30 text-sunset-orange hover:bg-purple-500/10"
        >
          <Settings className="w-4 h-4 mr-2" />
          Paramètres Audio
        </Button>
      </DialogTrigger>
      <DialogContent className="bg-dark-card border-sunset-orange/30 text-white max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-purple-500/20 border border-sunset-orange/30 flex items-center justify-center">
              <Waves className="w-5 h-5 text-purple-400" />
            </div>
            <DialogTitle className="text-xl text-white">Paramètres du Lecteur</DialogTitle>
          </div>
        </DialogHeader>

        <div className="space-y-6 mt-6">
          {/* Crossfade Duration */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-purple-200 text-sm font-medium">
                Durée du Crossfade
              </Label>
              <span className="text-sunset-orange text-sm font-mono">
                {localSettings.crossfadeDuration}s
              </span>
            </div>
            <Slider
              data-testid="crossfade-duration-slider"
              value={[localSettings.crossfadeDuration]}
              onValueChange={(value) => updateSetting('crossfadeDuration', value[0])}
              min={2}
              max={15}
              step={1}
              className="w-full"
            />
            <p className="text-sunset-orange/60 text-xs">
              Durée de la transition entre deux titres (2-15 secondes)
            </p>
          </div>

          {/* Crossfade Curve */}
          <div className="space-y-3">
            <Label className="text-purple-200 text-sm font-medium">
              Courbe de Crossfade
            </Label>
            <Select 
              value={localSettings.crossfadeCurve} 
              onValueChange={(value) => updateSetting('crossfadeCurve', value)}
            >
              <SelectTrigger 
                data-testid="crossfade-curve-select"
                className="bg-dark-input/50 border-sunset-orange/30 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-dark-input border-sunset-orange/30 text-white">
                <SelectItem value="linear">Linéaire (Standard)</SelectItem>
                <SelectItem value="easeIn">Ease In (Doux au début)</SelectItem>
                <SelectItem value="easeOut">Ease Out (Doux à la fin)</SelectItem>
                <SelectItem value="easeInOut">Ease In/Out (Doux aux deux bouts)</SelectItem>
                <SelectItem value="logarithmic">Logarithmique (Naturel)</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sunset-orange/60 text-xs">
              {getCurveDescription(localSettings.crossfadeCurve)}
            </p>
          </div>

          {/* Auto-play Mode */}
          <div className="space-y-3">
            <Label className="text-purple-200 text-sm font-medium">
              Comportement Auto-Play
            </Label>
            <Select 
              value={localSettings.autoPlayMode} 
              onValueChange={(value) => updateSetting('autoPlayMode', value)}
            >
              <SelectTrigger 
                data-testid="autoplay-mode-select"
                className="bg-dark-input/50 border-sunset-orange/30 text-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-dark-input border-sunset-orange/30 text-white">
                <SelectItem value="continuous">Continu (Enchaînement automatique)</SelectItem>
                <SelectItem value="stopAfterTrack">Arrêt après chaque titre</SelectItem>
                <SelectItem value="loopPlaylist">Boucle sur la playlist</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Pre-fade Start */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-purple-200 text-sm font-medium">
                Démarrage Anticipé (Pre-fade)
              </Label>
              <span className="text-sunset-orange text-sm font-mono">
                {localSettings.prefadeStart}s
              </span>
            </div>
            <Slider
              data-testid="prefade-start-slider"
              value={[localSettings.prefadeStart]}
              onValueChange={(value) => updateSetting('prefadeStart', value[0])}
              min={0}
              max={10}
              step={0.5}
              className="w-full"
            />
            <p className="text-sunset-orange/60 text-xs">
              Précharger le titre suivant X secondes avant la fin (0-10 sec)
            </p>
          </div>
        </div>

        <div className="flex gap-3 mt-6">
          <Button
            onClick={() => setOpen(false)}
            variant="outline"
            className="flex-1 border-sunset-orange/30 text-sunset-orange hover:bg-purple-500/10"
          >
            Annuler
          </Button>
          <Button
            data-testid="save-settings-button"
            onClick={handleSave}
            className="flex-1 bg-gradient-to-r from-sunset-orange to-sunset-secondary hover:from-purple-700 hover:to-blue-700 text-white"
          >
            Enregistrer
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const getCurveDescription = (curve) => {
  const descriptions = {
    linear: 'Transition uniforme du début à la fin',
    easeIn: 'Transition douce au début, rapide à la fin',
    easeOut: 'Transition rapide au début, douce à la fin',
    easeInOut: 'Transition douce au début et à la fin',
    logarithmic: 'Transition naturelle basée sur la perception humaine'
  };
  return descriptions[curve] || '';
};

export default PlayerSettings;
